'use client'

import { sendContactMail } from '@/app/libs/api/contact/api'
import CalendlyEmbed from '@/components/ui/CalendlyEmbed'
import Checkbox from '@/components/ui/inputs/Checkbox'
import Input from '@/components/ui/inputs/Input'
import TextArea from '@/components/ui/inputs/TextArea'
import { trackLeadFormSubmit } from '@/utils/tracking'
import { contactFormSchema } from '@/utils/validation/contact-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SiSap, SiShopify, SiShopware, SiWoocommerce, SiWordpress } from '@icons-pack/react-simple-icons'
import { IconArrowRight, IconArrowsExchange, IconCircleCheck, IconClock, IconCreditCard, IconDeviceMobile, IconDots, IconLoader2, IconPalette, IconSparkles } from '@tabler/icons-react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'
import { ReactNode, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'

export type ContactFormData = z.infer<typeof contactFormSchema>

const SERVICE_KEYS = ['shopify', 'woocommerce', 'wordpress', 'shopware', 'sap', 'stripe', 'takeover', 'ki', 'app', 'web', 'other'] as const
type ServiceKey = (typeof SERVICE_KEYS)[number]

const SERVICE_ICONS: Record<ServiceKey, ReactNode> = {
  shopify: <SiShopify size={18} />,
  woocommerce: <SiWoocommerce size={18} />,
  wordpress: <SiWordpress size={18} />,
  shopware: <SiShopware size={18} />,
  sap: <SiSap size={18} />,
  // Bewusst ein neutrales Icon: die Stripe-Bildmarke ist Teil des Stripe-Logos und
  // darf laut Co-Branding-Guidelines nur mit schriftlicher Freigabe verwendet werden.
  stripe: <IconCreditCard size={18} />,
  takeover: <IconArrowsExchange size={18} />,
  ki: <IconSparkles size={18} />,
  app: <IconDeviceMobile size={18} />,
  web: <IconPalette size={18} />,
  other: <IconDots size={18} />
}

// LPs verlinken mit ?service=shopify-takeover, ?service=sap-commerce etc.,
// hier wird der Query-Param auf einen Service-Chip abgebildet
function resolveServiceParam(param: string | null): ServiceKey | null {
  if (!param) return null
  const p = param.toLowerCase()
  if (p.includes('takeover')) return 'takeover'
  if (p.startsWith('stripe') || p.includes('payment') || p.includes('zahlung')) return 'stripe'
  if (p.startsWith('shopify')) return 'shopify'
  if (p.startsWith('woocommerce')) return 'woocommerce'
  if (p.startsWith('wordpress')) return 'wordpress'
  if (p.startsWith('shopware')) return 'shopware'
  if (p.startsWith('sap')) return 'sap'
  if (p.startsWith('ki') || p.startsWith('ai')) return 'ki'
  if (p.includes('app') || p.includes('mobile') || p.includes('react-native') || p.includes('expo')) return 'app'
  if (p.includes('web') || p.includes('brand') || p.includes('design')) return 'web'
  return 'other'
}

const SectionHeading = ({ index, children }: { index: string; children: ReactNode }) => (
  <div className="col-span-full flex flex-row items-baseline gap-x-6 border-b border-dashed border-accent pb-4">
    <span className="text-accent font-medium text-sm tracking-widest">{index}</span>
    <h3 className="text-white text-xl lg:text-2xl font-medium">{children}</h3>
  </div>
)

interface ContactFormProps {
  /**
   * Analytics-Quelle des Leads. Landingpages setzen hier ihren Pfad, damit
   * sich Anfragen der Kampagne zuordnen lassen statt pauschal auf 'kontakt'.
   */
  source?: string
  /** Vorauswahl des Service-Chips, wenn kein ?service=-Param vorliegt. */
  defaultService?: ServiceKey
  /** Grid-Klassen des <form>, damit LPs die Breite selbst bestimmen können. */
  className?: string
}

const ContactForm = ({ source = 'kontakt', defaultService, className }: ContactFormProps) => {
  const t = useTranslations('contact')
  const tForm = useTranslations('contact.form')
  const tForms = useTranslations('forms')
  const searchParams = useSearchParams()

  const [selectedService, setSelectedService] = useState<ServiceKey | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSent, setIsSent] = useState(false)
  // Honeypot: unsichtbares Feld, das nur Bots ausfüllen
  const [honeypot, setHoneypot] = useState('')

  const {
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  })

  // Vorauswahl aus dem ?service=-Param der Landingpages, sonst aus defaultService
  useEffect(() => {
    const resolved = resolveServiceParam(searchParams.get('service')) ?? defaultService
    if (resolved) {
      setSelectedService(resolved)
      setValue('service', tForm(`services.${resolved}`))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, defaultService])

  const selectService = (key: ServiceKey) => {
    setSelectedService(key)
    setValue('service', tForm(`services.${key}`), { shouldValidate: true })
  }

  const onSubmit = async (data: ContactFormData) => {
    // Bot hat das Honeypot-Feld ausgefüllt → stiller Fake-Erfolg, kein Versand
    if (honeypot) {
      setIsSent(true)
      return
    }
    setIsSubmitting(true)
    const res = await sendContactMail({ ...data, website: honeypot } as ContactFormData)
    setIsSubmitting(false)

    if (res?.success) {
      trackLeadFormSubmit(selectedService ? `${source}-${selectedService}` : source)
      setIsSent(true)
      reset()
    } else {
      toast.error(res?.error?.message || t('messageError'))
    }
  }

  const termsChecked = watch('terms', false)

  if (isSent) {
    return (
      <div className={`${className ?? 'col-span-full lg:col-span-6'} flex flex-col items-center justify-center text-center bg-KMRS-Dark border border-dashed border-accent rounded-3xl p-12 lg:p-16`}>
        <IconCircleCheck size={64} className="text-accent mb-8" stroke={1.5} />
        <h3 className="text-white text-2xl lg:text-3xl font-medium mb-4">{tForm('successTitle')}</h3>
        <p className="text-white/80 text-lg leading-6 max-w-md">{tForm('successText')}</p>
        <div className="mt-12 flex flex-col items-center gap-4">
          <p className="text-white/60 text-sm">{tForm('successAlt')}</p>
          <CalendlyEmbed
            variant="popup"
            text={tForm('successCta')}
            trackingSource="kontakt-success"
            className="bg-accent text-white px-8 py-4 rounded-3xl font-medium hover:bg-accent/90 transition-colors"
          />
        </div>
        <button onClick={() => setIsSent(false)} className="mt-10 text-white/50 text-sm underline-offset-4 hover:text-accent hover:underline transition-colors">
          {tForm('newRequest')}
        </button>
      </div>
    )
  }

  return (
    <form className={`${className ?? 'col-span-full lg:col-span-6'} grid grid-cols-2 gap-x-8 gap-y-8`} onSubmit={handleSubmit(onSubmit)}>
      {/* 01, Service */}
      <SectionHeading index="01">{tForm('sectionService')}</SectionHeading>
      <div className="col-span-full flex flex-row flex-wrap gap-3">
        {SERVICE_KEYS.map((key) => {
          const isSelected = selectedService === key
          return (
            <button
              key={key}
              type="button"
              onClick={() => selectService(key)}
              aria-pressed={isSelected}
              className={`flex flex-row items-center gap-x-2 px-5 py-3 rounded-3xl border text-base leading-none transition-all duration-200 cursor-pointer ${
                isSelected ? 'bg-accent border-accent text-white shadow-lg shadow-accent/20' : 'bg-transparent border-dashed border-KMRS-Light text-white/80 hover:border-accent hover:text-accent'
              }`}
            >
              {SERVICE_ICONS[key]}
              {tForm(`services.${key}`)}
            </button>
          )
        })}
      </div>
      <input type="hidden" {...register('service')} />
      {/* Honeypot, für Menschen unsichtbar, von Bots gern ausgefüllt */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      {/* 02, Kontakt */}
      <SectionHeading index="02">{tForm('sectionAbout')}</SectionHeading>
      <Input label={tForms('fields.firstName')} autoComplete="given-name" errors={errors} containerClassName="flex flex-col gap-y-4 col-span-2 md:col-span-1" labelClassName="text-white/70 text-sm" {...register('name')} />
      <Input label={tForms('fields.lastName')} autoComplete="family-name" errors={errors} containerClassName="flex flex-col gap-y-4 col-span-2 md:col-span-1" labelClassName="text-white/70 text-sm" {...register('surname')} />
      <Input label={tForms('fields.company')} autoComplete="organization" errors={errors} containerClassName="flex flex-col gap-y-4 col-span-2 md:col-span-1" labelClassName="text-white/70 text-sm" {...register('company')} />
      <Input label={tForms('fields.email')} type="email" inputMode="email" autoComplete="email" errors={errors} containerClassName="flex flex-col gap-y-4 col-span-2 md:col-span-1" labelClassName="text-white/70 text-sm" {...register('email')} />

      {/* 03, Nachricht */}
      <SectionHeading index="03">{tForm('sectionMessage')}</SectionHeading>
      <TextArea
        label={tForm('sectionMessageHint')}
        errors={errors}
        containerClassName="flex flex-col gap-y-4 col-span-full"
        textAreaClassName="min-h-40 max-h-40 resize-none"
        {...register('userMessage')}
      />

      <Checkbox
        containerClassName="flex flex-col col-span-full"
        {...register('terms')}
        label={
          <p className="text-xs [&_a]:text-accent leading-4 font-light">
            {t('privacyConsent.part1')}{' '}
            <Link target="_blank" href="/datenschutz">
              {t('privacyConsent.link')}
            </Link>{' '}
            {t('privacyConsent.part2')}
          </p>
        }
        errors={errors}
        name="terms"
        checked={termsChecked}
        onToggleValue={(value) => setValue('terms', !!value, { shouldValidate: true })}
      />

      <div className="col-span-full flex flex-col gap-y-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="group flex flex-row items-center justify-between bg-accent text-white p-8 w-full rounded-3xl font-medium text-lg leading-6 transition-all duration-200 disabled:bg-KMRS-Dark disabled:text-white/30 disabled:cursor-not-allowed enabled:hover:shadow-lg enabled:hover:shadow-accent/20 enabled:cursor-pointer"
        >
          {t('sendMessage')}
          {isSubmitting ? <IconLoader2 size={24} className="animate-spin" /> : <IconArrowRight size={24} className="transition-transform duration-200 group-hover:translate-x-2" />}
        </button>
        <p className="flex flex-row items-center gap-x-2 text-white/50 text-sm">
          <IconClock size={16} className="text-accent shrink-0" />
          {tForm('responseNote')}
        </p>
        {/* Niedrigschwellige Alternative direkt am Formular (nicht erst nach dem Absenden) */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-2 pt-1 text-white/60 text-sm">
          <span>Lieber direkt sprechen?</span>
          <CalendlyEmbed variant="popup" text="Kostenlosen Termin buchen" trackingSource="kontakt-form-alt" className="text-accent underline underline-offset-4 hover:text-accent/80 cursor-pointer" />
          <a href="tel:+4991147734580" className="text-accent underline underline-offset-4 hover:text-accent/80">
            +49 911 47734580
          </a>
        </div>
      </div>
    </form>
  )
}

export default ContactForm
